import React, { useMemo, useState } from 'react'
import * as Mui from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import SyncLockRoundedIcon from '@mui/icons-material/SyncLockRounded'
import DnsRoundedIcon from '@mui/icons-material/DnsRounded'
import { useNavigate } from 'react-router-dom'

import { CommonBtn, NumberPad } from '../../components'
import RUCHIVEDIKA_LOGO from '../../assets/ruchi-vedika-compressed.png'
import { getServerOptions } from '../../config/server-options'
import { loginIntoApp } from '../../services'
import { useAppDispatch } from '../../redux-store/hooks'
import { logIntoApp } from '../../redux-store/reducers'

interface PinLoginScreenProps {
  selectedServerUrl?: string | null
  onServerChange?: (serverUrl: string) => void
}

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const PinLoginScreen: React.FC<PinLoginScreenProps> = ({
  selectedServerUrl,
  onServerChange,
}) => {
  const navigate = useNavigate()
  const dispatcher = useAppDispatch()
  const theme = Mui.useTheme()
  const primaryColor = theme.palette.primary.main
  const activeServerUrl = selectedServerUrl ? normalizeUrl(selectedServerUrl) : ''

  const [employeeId, setEmployeeId] = useState('')
  const [pin, setPin] = useState('')
  const [isPin, setIsPin] = useState(false)
  const [loginError, setLoginError] = useState('')
  const [customServerUrl, setCustomServerUrl] = useState('')
  const [serverError, setServerError] = useState('')
  const [activeField, setActiveField] = useState<'employeeId' | 'pin'>(
    'employeeId',
  )

  const serverOptions = useMemo(() => {
    const options = getServerOptions()
    const hasActiveUrl = options.some((option) => option.url === activeServerUrl)

    if (activeServerUrl && !hasActiveUrl) {
      return [
        ...options,
        {
          id: `current-${activeServerUrl}`,
          name: 'Current custom',
          url: activeServerUrl,
        },
      ]
    }

    return options
  }, [activeServerUrl])

  const activeServerName =
    serverOptions.find((option) => option.url === activeServerUrl)?.name ||
    'Selected server'
  const empInputBorder =
    activeField === 'employeeId' ? `8px solid ${primaryColor}` : ''
  const pinInputBorder =
    activeField === 'pin' ? `8px solid ${primaryColor}` : ''

  const handleInputChange = (newInput: string) => {
    if (activeField === 'employeeId') {
      setEmployeeId(newInput)
    } else {
      setPin(newInput)
    }
  }

  const handleLogin = async () => {
    setLoginError('')

    if (employeeId.trim() === '' || pin.trim() === '') {
      setLoginError('Enter your employee details and password.')
      return
    }

    try {
      const response = await loginIntoApp(employeeId, pin)
      if (response && response.isSuccess) {
        dispatcher(
          logIntoApp({
            token: response?.data?.token,
            userId: response?.data?.userId,
          }),
        )
        navigate('/', { replace: true })
        return
      }

      setLoginError('Login failed. Please check your credentials and server.')
    } catch {
      setLoginError('Login failed. Please check the selected server and try again.')
    }
  }

  const handleServerChange = (serverUrl: string) => {
    if (!onServerChange) return
    onServerChange(serverUrl)
  }

  const handleCustomServer = () => {
    const normalizedUrl = normalizeUrl(customServerUrl)
    setServerError('')

    if (!normalizedUrl) {
      setServerError('Enter a server URL')
      return
    }

    try {
      new URL(normalizedUrl)
      handleServerChange(normalizedUrl)
    } catch {
      setServerError('Enter a valid server URL')
    }
  }

  return (
    <Mui.Box
      sx={{
        minHeight: '100vh',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, #fff7dd 45%, ${Mui.alpha(theme.palette.secondary.main, 0.2)} 100%)`,
      }}
    >
      <Mui.Grid container minHeight="100vh">
        <Mui.Grid
          item
          xs={false}
          md={8}
          lg={8}
          sx={{
            display: { xs: 'none', md: 'block' },
            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${Mui.alpha(theme.palette.secondary.main, 0.78)} 52%, ${theme.palette.primary.main} 100%)`,
          }}
        >
          <Mui.Stack
            height="100%"
            justifyContent="center"
            alignItems="center"
            sx={{ p: 5, position: 'relative', zIndex: 1 }}
          >
            <Mui.Typography
              variant="caption"
              fontWeight={900}
              sx={{
                alignSelf: 'flex-start',
                bgcolor: Mui.alpha(theme.palette.common.black, 0.38),
                border: `1px solid ${Mui.alpha(theme.palette.common.white, 0.22)}`,
                borderRadius: 2,
                color: theme.palette.common.white,
                px: 1.5,
                py: 0.75,
                position: 'absolute',
                left: 24,
                top: 24,
              }}
            >
              Express POS v1.2.5
            </Mui.Typography>

            <Mui.Box
              component="img"
              src={RUCHIVEDIKA_LOGO}
              alt="Ruchi Vedika"
              sx={{
                borderRadius: 3,
                maxWidth: 320,
                objectFit: 'contain',
              }}
            />
          </Mui.Stack>
        </Mui.Grid>

        <Mui.Grid item xs={12} md={4} lg={4}>
          <Mui.Stack
            minHeight="100vh"
            justifyContent="center"
            alignItems="center"
            sx={{ px: { xs: 2, sm: 4, md: 5 }, py: 4 }}
          >
            <Mui.Paper
              elevation={0}
              sx={{
                border: `1px solid ${Mui.alpha(theme.palette.secondary.main, 0.14)}`,
                borderRadius: 3,
                boxShadow: '0 26px 70px rgba(30,30,30,0.14)',
                maxWidth: 430,
                p: { xs: 2.5, sm: 3.5 },
                width: '100%',
              }}
            >
              <Mui.Stack gap={3}>
                <Mui.Stack alignItems="center" textAlign="center" gap={1}>
                  <Mui.Typography variant="h4" fontWeight={900} color="text.primary">
                    Welcome back
                  </Mui.Typography>
                  <Mui.Typography color="text.secondary">
                    Sign in to continue billing.
                  </Mui.Typography>
                </Mui.Stack>

                <Mui.Stack
                  gap={1.5}
                  sx={{
                    bgcolor: Mui.alpha(theme.palette.primary.main, 0.22),
                    border: `1px solid ${Mui.alpha(theme.palette.primary.main, 0.44)}`,
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Mui.Stack direction="row" alignItems="center" gap={1}>
                    <DnsRoundedIcon color="secondary" fontSize="small" />
                    <Mui.Box minWidth={0}>
                      <Mui.Typography fontWeight={900} fontSize="0.86rem">
                        Server
                      </Mui.Typography>
                      <Mui.Typography noWrap variant="caption" color="text.secondary">
                        {activeServerName}
                      </Mui.Typography>
                    </Mui.Box>
                  </Mui.Stack>

                  <Mui.TextField
                    select
                    fullWidth
                    label="Backend server"
                    size="small"
                    value={activeServerUrl}
                    onChange={(event) => handleServerChange(event.target.value)}
                    disabled={!onServerChange || !serverOptions.length}
                  >
                    {serverOptions.map((option) => (
                      <Mui.MenuItem key={option.id} value={option.url}>
                        <Mui.Box minWidth={0}>
                          <Mui.Typography noWrap fontWeight={800} fontSize="0.9rem">
                            {option.name}
                          </Mui.Typography>
                          <Mui.Typography noWrap variant="caption" color="text.secondary">
                            {option.url}
                          </Mui.Typography>
                        </Mui.Box>
                      </Mui.MenuItem>
                    ))}
                  </Mui.TextField>

                  <Mui.Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                    <Mui.TextField
                      error={Boolean(serverError)}
                      helperText={serverError || ' '}
                      label="Custom URL"
                      onChange={(event) => {
                        setServerError('')
                        setCustomServerUrl(event.target.value)
                      }}
                      placeholder="https://api.example.com"
                      size="small"
                      value={customServerUrl}
                      fullWidth
                    />
                    <Mui.Button
                      disabled={!onServerChange || !customServerUrl.trim()}
                      onClick={handleCustomServer}
                      variant="contained"
                      color="secondary"
                      sx={{
                        alignSelf: { xs: 'stretch', sm: 'flex-start' },
                        borderRadius: 1.5,
                        flexShrink: 0,
                        minHeight: 40,
                        px: 2.25,
                      }}
                    >
                      Use
                    </Mui.Button>
                  </Mui.Stack>
                </Mui.Stack>

                <Mui.Stack alignItems="center" gap={0.5}>
                  <Mui.Typography variant="subtitle1" fontWeight={900} color="secondary">
                    Choose Login Mode
                  </Mui.Typography>
                  <Mui.Box
                    onClick={() => setIsPin((prev) => !prev)}
                    sx={{
                      width: 76,
                      height: 34,
                      borderRadius: '999px',
                      backgroundColor: isPin ? 'success.main' : 'secondary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: isPin ? 'flex-end' : 'flex-start',
                      p: '3px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Mui.IconButton
                      disableRipple
                      size="small"
                      sx={{
                        bgcolor: 'CommonWhite.main',
                        color: isPin ? 'success.main' : 'secondary.main',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        transition: 'transform 0.3s ease',
                        transform: isPin ? 'rotate(360deg)' : 'rotate(0deg)',
                        '&:hover': {
                          backgroundColor: 'CommonWhite.main',
                        },
                      }}
                    >
                      {isPin ? (
                        <AccountCircleIcon fontSize="small" />
                      ) : (
                        <SyncLockRoundedIcon fontSize="small" />
                      )}
                    </Mui.IconButton>
                  </Mui.Box>
                </Mui.Stack>

                {loginError && <Mui.Alert severity="error">{loginError}</Mui.Alert>}

                <Mui.Stack spacing={1.25}>
                  <div
                    style={{
                      borderRight: empInputBorder,
                      width: '100%',
                      padding: 5,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Mui.TextField
                      label={isPin ? 'Employee Id' : 'Employee Email'}
                      variant="outlined"
                      type={isPin ? 'number' : 'text'}
                      value={employeeId}
                      color="secondary"
                      onFocus={() => setActiveField('employeeId')}
                      onChange={(event) => setEmployeeId(event.target.value)}
                      fullWidth
                    />
                  </div>
                  <div
                    style={{
                      borderRight: pinInputBorder,
                      width: '100%',
                      padding: 5,
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Mui.TextField
                      label={isPin ? 'PIN' : 'Password'}
                      type="password"
                      variant="outlined"
                      color="secondary"
                      value={pin}
                      onFocus={() => setActiveField('pin')}
                      onChange={(event) => setPin(event.target.value)}
                      inputProps={{
                        pattern: '[0-9]*',
                        maxLength: 10,
                      }}
                      fullWidth
                    />
                  </div>

                  <Mui.Collapse in={isPin} timeout={300} unmountOnExit>
                    <NumberPad
                      onChange={handleInputChange}
                      value={activeField === 'employeeId' ? employeeId : pin}
                    />
                  </Mui.Collapse>

                  <CommonBtn
                    variant="contained"
                    color="secondary"
                    onClick={handleLogin}
                    endIcon={<SendIcon />}
                  >
                    Login
                  </CommonBtn>
                </Mui.Stack>
              </Mui.Stack>
            </Mui.Paper>
          </Mui.Stack>
        </Mui.Grid>
      </Mui.Grid>
    </Mui.Box>
  )
}

export { PinLoginScreen as LoginScreen }
